import { seedPaymentMethods } from "@/data/reference";
import { db } from "./hooks";

/**
 * First-run and every-run initialisation: default payment methods (idempotent) and a request
 * for persistent storage so the browser does not evict the data under storage pressure.
 */
export async function initialiseApp(): Promise<void> {
  await seedPaymentMethods(db);
  if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
    const granted = await navigator.storage.persist();
    await db.settings.put({ key: "storage.persistent", value: granted });
  }
}

/**
 * Opening the local database after an app update may have to wait for other open copies of the
 * app (another tab, or the installed app suspended in the background) to let go of the previous
 * version. Without this the app would show "Caricamento…" forever: the user must be told why.
 *  - onBlocked: this copy is waiting for another copy to close.
 *  - onOutdated: another copy has upgraded the database; this one must be reloaded (Dexie closes
 *    the connection by default so that the other copy's upgrade can proceed).
 */
export function watchDatabase({ onBlocked, onOutdated }: { onBlocked: () => void; onOutdated: () => void }): () => void {
  const blocked = () => onBlocked();
  const outdated = (event: IDBVersionChangeEvent) => {
    if (event.newVersion) onOutdated();
  };
  db.on("blocked", blocked);
  db.on("versionchange", outdated);
  return () => {
    db.on("blocked").unsubscribe(blocked);
    db.on("versionchange").unsubscribe(outdated);
  };
}

export async function storageStatus(): Promise<{ persistent: boolean | null; usage: number | null; quota: number | null }> {
  const persistent = navigator.storage?.persisted ? await navigator.storage.persisted() : null;
  const estimate = navigator.storage?.estimate ? await navigator.storage.estimate() : null;
  return { persistent, usage: estimate?.usage ?? null, quota: estimate?.quota ?? null };
}
